"use client"

import { useState, useEffect, useMemo, useCallback } from "react"
import { useQuery } from "@tanstack/react-query"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEdit, faTrash, faEye } from "@fortawesome/free-solid-svg-icons"

import Card from "../../components/inventory/modules/Card/Card"
import Button from "../../components/inventory/modules/Button/Button"
import SearchBar from "../../components/shared-modules/SearchBar"
import LoadingState from "../../components/inventory/LoadingState"
import Table from "../../components/inventory/modules/table/table"
import Badge from "../../components/inventory/modules/Badge/Badge"
import styles from "./InventoryDashboardPage.module.css"

import devicesService from "../../services/inventory/devices"
import { formatDateOnly } from "../../utils/dateFormatters"

function useDebounced(value, delay = 500) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

const showOrNA = (val) => {
  if (val === null || val === undefined || (typeof val === "string" && val.trim() === "")) return "N/A"
  return val
}

/**
 * TenantDeviceDistributionPage
 * - Tenant-only view (no warehouse/admin logic)
 * - Determines tenant id from sessionStorage.inventory_active_account (if present) or user.current_account
 * - Fetches tenant stats, active devices, deactivated devices and available devices for that tenant
 */
const TenantDeviceDistributionPage = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = useDebounced(searchTerm, 500)

  const [selectedStatusFilter, setSelectedStatusFilter] = useState("all")
  const [selectedFilter, setSelectedFilter] = useState(null)
  const [selectedDevices, setSelectedDevices] = useState([])

  const [activePage, setActivePage] = useState(1)
  const [deactivatedPage, setDeactivatedPage] = useState(1)
  const [availablePage, setAvailablePage] = useState(1) // NEW

  const [activePaginationLoading, setActivePaginationLoading] = useState(false)
  const [deactivatedPaginationLoading, setDeactivatedPaginationLoading] = useState(false)
  const [availablePaginationLoading, setAvailablePaginationLoading] = useState(false) // NEW

  // determine tenant/account id to show
  const [activeAccount, setActiveAccount] = useState(() => {
    try {
      const s = localStorage.getItem("inventory_active_account")
      return s ? JSON.parse(s) : null
    } catch {
      return null
    }
  })

  useEffect(() => {
    const onStorage = () => {
      try {
        const s = localStorage.getItem("inventory_active_account")
        setActiveAccount(s ? JSON.parse(s) : null)
      } catch {
        setActiveAccount(null)
      }
    }
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [])

  // fallback to logged-in user's current account if inventory_active_account isn't set
  const rawLocalUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {}
    } catch {
      return {}
    }
  })()

  // prefer activeAccount.id (if set), else user.current_account
  const tenantIdFromActiveAccount = activeAccount && activeAccount.id ? activeAccount.id : null
  const tenantIdFromUser = rawLocalUser?.current_account ?? rawLocalUser?.account?.id ?? null

  const effectiveTenantId = tenantIdFromActiveAccount || tenantIdFromUser || null

  // Keep a small selectedTenant for UI interactions (but page is tenant-specific so we default it)
  const [selectedTenant, setSelectedTenant] = useState(() => effectiveTenantId)

  useEffect(() => {
    if (effectiveTenantId && !selectedTenant) {
      setSelectedTenant(effectiveTenantId)
    }
  }, [effectiveTenantId, selectedTenant])

  // tenant-level stats
  const { data: tenantStats, isLoading: tenantStatsLoading } = useQuery(
    ["tenantStats", effectiveTenantId],
    () => devicesService.getTenantDevicesStats(effectiveTenantId),
    {
      enabled: !!effectiveTenantId,
      staleTime: 30 * 1000,
      onError: (err) => console.error("tenantStats error", err),
    },
  )

  const selectedLocationId = selectedFilter?.location_id ?? selectedFilter?.id ?? null

  // For this tenant-only page, if the active account is a sub account we might allow location scoping;
  // otherwise locationParamForApi is null to query tenant-level devices.
  const switchedTenantId = effectiveTenantId
  const locationParamForApi = selectedLocationId ?? null

  const {
    data: activeDevicesData,
    isLoading: activeDevicesLoading,
    refetch: refetchActiveDevices,
  } = useQuery(
    ["tenantDevices", switchedTenantId, "assigned", locationParamForApi, activePage, debouncedSearch],
    async () => {
      if (!switchedTenantId) {
        return { meta: { total_items: 0, total_pages: 0, current_page: 1, next: null, previous: null }, results: [] }
      }

      return devicesService.getActiveTenantDevices(switchedTenantId, {
        locationId: locationParamForApi,
        page: activePage,
        search: debouncedSearch,
      })
    },
    {
      enabled: !!switchedTenantId,
      keepPreviousData: true,
      onError: (err) => console.error("active devices error", err),
    },
  )

  const {
    data: deactivatedDevicesData,
    isLoading: deactivatedDevicesLoading,
    refetch: refetchDeactivatedDevices,
  } = useQuery(
    ["tenantDevices", switchedTenantId, "unlinked", locationParamForApi, deactivatedPage, debouncedSearch],
    async () => {
      if (!switchedTenantId) {
        return { meta: { total_items: 0, total_pages: 0, current_page: 1, next: null, previous: null }, results: [] }
      }
      return devicesService.getDeactivatedTenantDevices(switchedTenantId, {
        locationId: locationParamForApi,
        page: deactivatedPage,
        search: debouncedSearch,
      })
    },
    {
      enabled: !!switchedTenantId,
      keepPreviousData: true,
      onError: (err) => console.error("deactivated devices error", err),
    },
  )

  // NEW: Available devices query
  const {
    data: availableDevicesData,
    isLoading: availableDevicesLoading,
    refetch: refetchAvailableDevices,
  } = useQuery(
    ["tenantDevices", switchedTenantId, "available", locationParamForApi, availablePage, debouncedSearch],
    async () => {
      if (!switchedTenantId) {
        return { meta: { total_items: 0, total_pages: 0, current_page: 1, next: null, previous: null }, results: [] }
      }
      return devicesService.getAvailableTenantDevices(switchedTenantId, {
        locationId: locationParamForApi,
        page: availablePage,
        search: debouncedSearch,
      })
    },
    {
      enabled: !!switchedTenantId,
      keepPreviousData: true,
      onError: (err) => console.error("available devices error", err),
    },
  )

  // Build office/location distribution from tenantStats
  const officeDistributionData = useMemo(() => {
    if (!tenantStats) return []
    // If tenantStats has devices.by_location
    if (tenantStats?.devices?.by_location) {
      return tenantStats.devices.by_location.map((loc) => ({
        id: loc.location_id ?? `loc-${loc.location_name ?? "unassigned"}`,
        office: tenantStats?.tenant_name ?? "Tenant",
        location: loc.location_name ?? "Unassigned",
        totalDevices: loc.total ?? 0,
        active: loc.assigned ?? 0,
        deactivated: loc.unlinked ?? 0,
        ordered: (loc.total_bulk_orders ?? 0) + (loc.total_dropship_orders ?? 0),
        locations: [],
        location_id: loc.location_id ?? null,
      }))
    }

    // Fallback: maybe tenantStats contains totals
    if (tenantStats?.devices?.total_devices !== undefined) {
      return [
        {
          id: tenantStats?.tenant_id ?? "tenant",
          office: tenantStats?.tenant_name ?? "Tenant",
          location: "",
          totalDevices: tenantStats.devices.total_devices,
          active: tenantStats.devices.assigned ?? 0,
          deactivated: tenantStats.devices.unlinked ?? 0,
          ordered: (tenantStats.bulk_orders?.total ?? 0) + (tenantStats.dropship_orders?.total ?? 0),
          locations: [],
          location_id: null,
        },
      ]
    }

    return []
  }, [tenantStats])

  const activeResults = activeDevicesData?.results ?? (Array.isArray(activeDevicesData) ? activeDevicesData : [])
  const activeMeta = activeDevicesData?.meta ?? null

  const deactivatedResults = deactivatedDevicesData?.results ?? (Array.isArray(deactivatedDevicesData) ? deactivatedDevicesData : [])
  const deactivatedMeta = deactivatedDevicesData?.meta ?? null

  const availableResults = availableDevicesData?.results ?? (Array.isArray(availableDevicesData) ? availableDevicesData : [])
  const availableMeta = availableDevicesData?.meta ?? null

  const officeDistributionColumns = useMemo(() => {
    const baseColumns = []

    // tenant-only view: show location column
    baseColumns.push({ key: "location", label: "Location", alignLeft: true })

    baseColumns.push(
      { key: "totalDevices", label: "Total Devices", alignLeft: true },
      { key: "active", label: "Active (Assigned)", alignLeft: true },
      { key: "deactivated", label: "Deactivated", alignLeft: true },
      { key: "ordered", label: "Ordered", alignLeft: true },
      {
        key: "actions",
        label: "Actions",
        sorting: false,
        render: (office) => (
          <Button
            variant={selectedFilter?.id === office.id ? "primary" : "secondary"}
            size="small"
            onClick={(e) => {
              e.stopPropagation()

              setSelectedFilter({
                id: office.id,
                office: office.office,
                location: office.location,
                location_id: office.location_id ?? null,
              })
              // set selected tenant location to filter devices
              setSelectedTenant(switchedTenantId)
              setActivePage(1)
              setDeactivatedPage(1)
              setAvailablePage(1) // NEW: reset available page
            }}
          >
            <FontAwesomeIcon icon={faEye} style={{ marginRight: "4px" }} />
            View Inventory
          </Button>
        ),
      },
    )
    return baseColumns
  }, [selectedFilter, switchedTenantId])

  const deviceStatusBadge = (status) => {
    if (!status) return { label: "N/A", variant: "default" }
    const s = String(status).toLowerCase()
    if (s === "assigned" || s === "available" || s === "active") return { label: "Assigned", variant: "success" }
    if (s === "unlinked" || s === "inactive" || s === "deactivated") return { label: "Deactivated", variant: "error" }

    return { label: showOrNA(status), variant: "default" }
  }

  const activeDevicesColumns = [
    {
      key: "sub_account",
      label: "Sub Account",
      alignLeft: true,
      render: (r) =>
        // prefer selected location if present, then selected office, then server fields
        showOrNA(selectedFilter?.location || selectedFilter?.office || r?.sub_account_name || r?.tenant_name || r?.organization || ""),
    },
    { key: "patient_name", label: "Patient Name", alignLeft: true, render: (r) => showOrNA(r?.patient_name || r?.patient_id || "") },
    { key: "imei", label: "IMEI", alignLeft: true, render: (r) => showOrNA(r?.imei) },
    {
      key: "device_type",
      label: "Device Type",
      alignLeft: true,
      render: (r) =>
        typeof r?.device_type === "object" ? showOrNA(r.device_type?.name) : showOrNA(r?.name || r?.device_type),
    },
    { key: "added_at", label: "Date Device Added", alignLeft: true, render: (r) => formatDateOnly(r?.added_at) },
    {
      key: "status",
      label: "Status",
      alignLeft: true,
      render: (r) => {
        const b = deviceStatusBadge(r?.status);
        return <Badge variant={b.variant}>{b.label}</Badge>;
      },
    },
    {
      key: "latest_reading",
      label: "Latest Reading",
      alignLeft: true,
      render: (r) => formatDateOnly(r?.latest_reading_at || r?.updated_at || null),
    },
  ];

  const deactivatedDevicesColumns = [
    {
      key: "sub_account",
      label: "Sub Account",
      alignLeft: true,
      render: (r) => showOrNA(selectedFilter?.location || selectedFilter?.office || r?.sub_account_name || r?.tenant_name || r?.organization || ""),
    },
    { key: "patient_name", label: "Patient Name", alignLeft: true, render: (r) => showOrNA(r?.patient_name || r?.patient_id || "") },
    { key: "imei", label: "IMEI", alignLeft: true, render: (r) => showOrNA(r?.imei) },
    {
      key: "device_type",
      label: "Device Type",
      alignLeft: true,
      render: (r) =>
        typeof r?.device_type === "object" ? showOrNA(r.device_type?.name) : showOrNA(r?.name || r?.device_type),
    },
    { key: "date_deactivated", label: "Date Deactivated", alignLeft: true, render: (r) => formatDateOnly(r?.added_at || r?.updated_at) },
    { key: "reason", label: "Reason", alignLeft: true, render: (r) => showOrNA(r?.unlinked_reason || r?.reason || "") },
    {
      key: "status",
      label: "Status",
      alignLeft: true,
      render: (r) => {
        const b = deviceStatusBadge(r?.status);
        return <Badge variant={b.variant}>{b.label}</Badge>;
      },
    },
  ];
  const availableDevicesColumns = activeDevicesColumns // reuse same columns for available devices

  const ordersColumns = [
    { key: "productType", label: "Product Type", alignLeft: true },
    { key: "quantity", label: "Quantity", alignLeft: true },
    { key: "deliveredBy", label: "Delivered By", alignLeft: true },
    {
      key: "status",
      label: "Status",
      alignLeft: true,
      render: (order) => {
        const getBadgeVariant = (status) => {
          if (status === "Delivered") return "success"
          if (status === "In Box") return "warning"
          return "default"
        }
        return (
          <Badge variant={getBadgeVariant(order.status)} size="small">
            {order.status}
          </Badge>
        )
      },
    },
  ]

  const handleSelectionChange = (selectedIds) => {
    setSelectedDevices(selectedIds)
  }

  const handleActivePageChange = useCallback(
    (which) => {
      setActivePaginationLoading(true)
      if (typeof which === "string" && which.startsWith("http")) {
        setActivePage(which)
      } else {
        setActivePage(Number(which) || 1)
      }
      setTimeout(() => setActivePaginationLoading(false), 800)
    },
    [setActivePage],
  )

  const handleDeactivatedPageChange = useCallback(
    (which) => {
      setDeactivatedPaginationLoading(true)
      if (typeof which === "string" && which.startsWith("http")) {
        setDeactivatedPage(which)
      } else {
        setDeactivatedPage(Number(which) || 1)
      }
      setTimeout(() => setDeactivatedPaginationLoading(false), 800)
    },
    [setDeactivatedPage],
  )

  // NEW: available page handler
  const handleAvailablePageChange = useCallback(
    (which) => {
      setAvailablePaginationLoading(true)
      if (typeof which === "string" && which.startsWith("http")) {
        setAvailablePage(which)
      } else {
        setAvailablePage(Number(which) || 1)
      }
      setTimeout(() => setAvailablePaginationLoading(false), 800)
    },
    [setAvailablePage],
  )

  useEffect(() => {
    setActivePage(1)
    setDeactivatedPage(1)
    setAvailablePage(1) // reset available page on filter/search change
  }, [debouncedSearch, selectedLocationId, switchedTenantId])

  // If no tenant id resolved, show a friendly message
  if (!effectiveTenantId) {
    return (
      <div className={styles.device_management_page}>
        <div className={styles.page_header}>
          <div className={styles.header_left}>
            <h1 className={styles.page_title}>Device Distribution</h1>
            <p className={styles.page_subtitle}>Tenant not selected or not available.</p>
          </div>
        </div>

        <div className={styles.section}>
          <Card title="No Tenant Selected">
            <p>Please select an account in inventory switcher or ensure your user has a tenant assigned.</p>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.device_management_page}>
      <div className={styles.page_header}>
        <div className={styles.header_left}>
          <h1 className={styles.page_title}>Device Distribution</h1>
          <p className={styles.page_subtitle}>Monitor and distribute devices for this account</p>
        </div>
      </div>

      <div className={styles.section}>
        <Card title="Location Distribution" className={styles.inventory_card}>
          <Table
            columns={officeDistributionColumns}
            data={officeDistributionData}
            height={300}
            isRowClickable={false}
            loading={tenantStatsLoading}
          />
        </Card>
      </div>

      {/* <div className={styles.section}>
        <Card title="Available Devices" className={styles.supplies_card}>
          <Table
            columns={availableDevicesColumns}
            data={availableResults}
            meta={availableMeta}
            pagination={true}
            paginationLoading={availablePaginationLoading}
            onPageChange={handleAvailablePageChange}
            height={400}
            isRowClickable={false}
            loading={availableDevicesLoading}
            onSelectionChange={handleSelectionChange}
            selectedIds={selectedDevices}
          />
        </Card>
      </div> */}

      <div className={styles.section}>
        <Card title="Active Devices" className={styles.supplies_card}>
          <Table
            columns={activeDevicesColumns}
            data={activeResults}
            meta={activeMeta}
            pagination={true}
            paginationLoading={activePaginationLoading}
            onPageChange={handleActivePageChange}
            height={400}
            isRowClickable={false}
            loading={activeDevicesLoading}
            onSelectionChange={handleSelectionChange}
            selectedIds={selectedDevices}
          />
        </Card>
      </div>

      <div className={styles.section}>
        <Card title="Deactivated Devices" className={styles.supplies_card}>
          <Table
            columns={deactivatedDevicesColumns}
            data={deactivatedResults}
            meta={deactivatedMeta}
            pagination={true}
            paginationLoading={deactivatedPaginationLoading}
            onPageChange={handleDeactivatedPageChange}
            height={400}
            isRowClickable={false}
            loading={deactivatedDevicesLoading}
            onSelectionChange={handleSelectionChange}
            selectedIds={selectedDevices}
          />
        </Card>
      </div>
    </div>
  )
}

export default TenantDeviceDistributionPage
